import { Editor, Element, Node, Path, Range, Text, Transforms } from '../core';
import type { Point } from '../core';
import { HEADING_PLUGIN_ID } from '../plugins/heading/types';
import {
  matchHeadingLikePrefix,
  normalizeHeadingLabel,
} from '../plugins/headingDecoration/matcher';
import { LINE_BREAK_PLUGIN_ID } from '../plugins/lineBreak/types';
import { PARAGRAPH_PLUGIN_ID } from '../plugins/paragraph/types';
import { isInlineParagraph } from '../plugins/paragraph/utils';
import { PROPER_NOUNS_ARRAY } from './normalizationHelpers';

/**
 * RP-6449 — the case of the first word after a dictated colon:
 *
 * 1. A section header or label that starts a line or entry capitalizes the next word
 *    (`Comparison: None.`, `Lungs: Clear.`).
 * 2. A mid-sentence colon lowercases the next word (`Two findings are notable: a 4 mm nodule`).
 * 3. A case-locked token (eponym, acronym, lexicon term, unit or symbol) keeps its own case and
 *    overrides both (`Follow-up is recommended: MRI`, `Renal function: eGFR 45`).
 *
 * The rules run once over the stitched text, before normalization, so the result depends on the
 * text around each colon and not on how the dictation was split into chunks or text nodes.
 * `normalizeTextNodeAtPath` leaves colon case alone (`deferColonCase`) while these rules apply.
 *
 * Section headers are the same with REPORTER_HEADING_NODES_DEPRECATED on or off:
 * - Off: a heading node, or a section keyword label in a paragraph. The heading plugin turns that
 *   paragraph into a heading node during normalization, after these rules have run, so the keyword
 *   is how heading nodes stay the source of truth.
 * - On: a section keyword label. Heading nodes still count while legacy ones can appear in the
 *   document.
 * Any other short label (`isShortColonLabel`) also capitalizes in both states.
 */

export interface ColonCaseRulesOptions {
  /** Normalised section keywords (`buildHeadingLabelSet`). */
  headingLabelSet: Set<string>;
}

interface BlockCharacter {
  char: string;
  /** Where the character sits in the editor. Null for the separator standing in for a line break. */
  point: Point | null;
}

// A label is short and has no linking verb: `Lungs:`, `Right ovary/adnexa:`, `Renal function:`.
// Longer runs, or ones with a linking verb, read as running text: `Two findings are notable:`,
// `Follow-up is recommended:`. Tunable.
export const MAX_COLON_LABEL_WORDS = 3;
const LINKING_VERBS = new Set(['is', 'are', 'was', 'were']);

// Common radiology eponyms. Hyphenated ones (`Salter-Harris`) already pass the mixed-case check
// in `isCaseLockedWord`. Tunable.
const EPONYMS = [
  'Alzheimer',
  'Baker',
  'Barrett',
  'Bosniak',
  'Chiari',
  'Crohn',
  'Doppler',
  'Fleischner',
  'Hodgkin',
  'Hounsfield',
  'Kerley',
  'Modic',
  'Paget',
  'Parkinson',
  'Schmorl',
  'Valsalva',
  'Wilms',
];
// Words that keep their own case on top of the shape checks in `isCaseLockedWord`. Adds the
// title-case terms from asr-plex's canonical term registry (`Isovue`, `Baker's cyst`).
const CASE_LOCKED_WORDS = new Set(
  [...PROPER_NOUNS_ARRAY, 'I', 'Isovue', ...EPONYMS].map((word) => word.toLowerCase())
);
const UNITS_OF_MEASURE = new Set([
  'cm',
  'mm',
  'nm',
  'kg',
  'mg',
  'ml',
  'cc',
  'l',
  'dl',
  'g',
  'mcg',
  'mol',
  'mmol',
  's',
  'ms',
  'min',
  'Gy',
  'Sv',
  'Bq',
  'Hz',
  'Pa',
]);
const INLINE_WHITESPACE = /[ \t\u00a0]/;
const OPENING_DELIMITERS = /[(["'“‘]/;
// Letter-led, with no sentence punctuation. Unlike the heading matcher, digits may appear anywhere:
// `Segment 4:`, `BI-RADS 2:`, `L4-L5 and L5-S1:`.
const LABEL_SHAPE = /^\s*[A-Za-z][A-Za-z0-9 ,&/'–-]*:$/;

export const isShortColonLabel = (label: string): boolean => {
  const words = label
    .replace(/:$/, '')
    .split(/\s+/)
    .filter((word) => /[A-Za-z0-9]/.test(word));
  return (
    words.length > 0 &&
    words.length <= MAX_COLON_LABEL_WORDS &&
    !words.some((word) => LINKING_VERBS.has(word.toLowerCase()))
  );
};

export const isCaseLockedWord = (word: string): boolean => {
  const bareWord = word.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '').replace(/['’]s$/, '');
  if (bareWord === '') return true;
  if (CASE_LOCKED_WORDS.has(bareWord.toLowerCase())) return true;
  // Units, including compound ones and exponents: `cm/s`, `ml/kg`, `mm²/s`.
  if (UNITS_OF_MEASURE.has(bareWord.split('/')[0].replace(/[²³¹⁰-⁹⁻]+$/, ''))) {
    return true;
  }
  // Alphanumeric tokens: `L4-L5`, `HbA1c`, `T2`.
  if (/\d/.test(bareWord)) return true;
  // Acronyms: `MRI`, `PET/CT`, `LI-RADS`.
  if (/^[A-Z]{2,}/.test(bareWord)) return true;
  // Mixed case: `eGFR`, `mRNA`, `pH`, `McBurney`.
  return /[A-Z]/.test(bareWord.slice(1));
};

/**
 * Applies the colon case rules to every colon in `range`, and to the first word after a colon when
 * that word is in `range`. Colons elsewhere in the document are left alone.
 */
export const applyColonCaseRules = (
  editor: Editor,
  range: Range,
  options: ColonCaseRulesOptions
): void => {
  const blockEntries = Array.from(
    Editor.nodes(editor, {
      at: range,
      match: (node) =>
        Element.isElement(node) &&
        (node.type === PARAGRAPH_PLUGIN_ID || node.type === HEADING_PLUGIN_ID),
    })
  );

  Editor.withoutNormalizing(editor, () => {
    for (const [block, path] of blockEntries) {
      if (!Element.isElement(block)) continue;
      const characters = getBlockCharacters(block, path);
      const text = characters.map(({ char }) => char).join('');

      // Flag off, a colon dictated at the end of a heading node applies to the value in the inline
      // paragraph after it, even if that value was already there.
      const lastCharIndex = text.trimEnd().length - 1;
      if (
        block.type === HEADING_PLUGIN_ID &&
        text[lastCharIndex] === ':' &&
        isInRange(range, characters[lastCharIndex].point)
      ) {
        capitalizeInlineParagraphAfter(editor, path);
      }

      // Flag off, a heading node holds `Label:` and its value sits in the inline paragraph after it.
      if (isInlineParagraph(block) && isAfterHeadingNode(editor, path)) {
        const wordStart = getNextWordStart(text, -1);
        if (wordStart != null && isInRange(range, characters[wordStart].point)) {
          setFirstLetterCase(editor, characters, text, wordStart, { isLabel: true });
        }
      }

      for (let colonIndex = text.indexOf(':'); colonIndex !== -1; ) {
        const wordStart = getNextWordStart(text, colonIndex);
        if (
          wordStart != null &&
          (isInRange(range, characters[colonIndex].point) ||
            isInRange(range, characters[wordStart].point))
        ) {
          setFirstLetterCase(editor, characters, text, wordStart, {
            isLabel: isLabelColon(block, text, colonIndex, options),
          });
        }
        colonIndex = text.indexOf(':', colonIndex + 1);
      }
    }
  });
};

const isLabelColon = (
  block: Element,
  text: string,
  colonIndex: number,
  { headingLabelSet }: ColonCaseRulesOptions
): boolean => {
  if (block.type === HEADING_PLUGIN_ID) return true;

  const entry = text.slice(getEntryStart(text, colonIndex, headingLabelSet), colonIndex + 1);
  if (headingLabelSet.has(normalizeHeadingLabel(entry))) return true;
  return LABEL_SHAPE.test(entry) && isShortColonLabel(entry);
};

// A label starts a line (the block start or a line break) or an entry (a new sentence on the same
// line, as in `Uterus: Normal. Right ovary/adnexa: Normal.`). A section keyword before it on the
// same line doesn't count (`Findings: Lungs: Clear.`), as when the keyword is a heading node.
const getEntryStart = (text: string, colonIndex: number, headingLabelSet: Set<string>): number => {
  const lineStart = text.lastIndexOf('\n', colonIndex - 1) + 1;
  let entryStart = lineStart;
  for (const sentenceEnd of text.slice(lineStart, colonIndex).matchAll(/[.?!]\s+/g)) {
    entryStart = lineStart + (sentenceEnd.index ?? 0) + sentenceEnd[0].length;
  }
  const keyword = matchHeadingLikePrefix(text.slice(entryStart, colonIndex), headingLabelSet);
  return keyword == null ? entryStart : entryStart + keyword.end;
};

// Only a letter on the same line starts a word worth recasing, after any opening bracket or quote
// (`Lungs: (Clear).`); digits (`2:1`, `14:30`), other symbols and line breaks don't.
const getNextWordStart = (text: string, colonIndex: number): number | null => {
  let index = colonIndex + 1;
  while (index < text.length && INLINE_WHITESPACE.test(text[index])) index++;
  while (index < text.length && OPENING_DELIMITERS.test(text[index])) index++;
  return index < text.length && /[A-Za-z]/.test(text[index]) ? index : null;
};

const setFirstLetterCase = (
  editor: Editor,
  characters: BlockCharacter[],
  text: string,
  wordStart: number,
  { isLabel }: { isLabel: boolean }
) => {
  const word = text.slice(wordStart).match(/^\S+/)?.[0] ?? '';
  const point = characters[wordStart].point;
  if (point == null || isCaseLockedWord(word)) return;

  const letter = text[wordStart];
  const newLetter = isLabel ? letter.toUpperCase() : letter.toLowerCase();
  if (newLetter === letter) return;

  // Insert the new letter after the old one before deleting it, so the selection doesn't shift.
  Transforms.insertText(editor, newLetter, { at: { ...point, offset: point.offset + 1 } });
  Transforms.delete(editor, { at: point });
};

// Line breaks can also sit inside inline elements, such as a field's bookmark, so this walks every
// level.
const getBlockCharacters = (element: Element, path: Path): BlockCharacter[] =>
  element.children.flatMap((child, index): BlockCharacter[] => {
    const childPath = [...path, index];
    if (Text.isText(child)) {
      return child.text.split('').map((char, offset) => ({
        char,
        point: { path: childPath, offset },
      }));
    }
    if (child.type === LINE_BREAK_PLUGIN_ID) return [{ char: '\n', point: null }];
    return getBlockCharacters(child, childPath);
  });

const capitalizeInlineParagraphAfter = (editor: Editor, headingPath: Path) => {
  const nextPath = Path.next(headingPath);
  if (!Node.has(editor, nextPath)) return;
  const next = Node.get(editor, nextPath);
  if (!Element.isElement(next) || !isInlineParagraph(next)) return;

  const characters = getBlockCharacters(next, nextPath);
  const text = characters.map(({ char }) => char).join('');
  const wordStart = getNextWordStart(text, -1);
  if (wordStart != null) setFirstLetterCase(editor, characters, text, wordStart, { isLabel: true });
};

export const isAfterHeadingNode = (editor: Editor, path: Path): boolean => {
  if (!Path.hasPrevious(path)) return false;
  const previous = Node.get(editor, Path.previous(path));
  return (
    Element.isElement(previous) &&
    previous.type === HEADING_PLUGIN_ID &&
    Node.string(previous).trimEnd().endsWith(':')
  );
};

const isInRange = (range: Range, point: Point | null): boolean =>
  point != null && Range.includes(range, point);
